import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, X } from '@phosphor-icons/react';

import { paths } from '../../routes/paths';
import pt from '../../locales/pt';
import { isCycleCloseDue } from '../../utils/cycleCloseAlert';
import { cn } from '../../utils/cn';
import { dismissAlertId, readDismissedAlertIds } from '../../utils/dismissedAlerts';

/**
 * Z1 — `.pen` → `ds/card/cycle-header` › `banner-slot` › `ds/message--warning`
 * com os overrides da instância: pilha **vertical** `gap $space-3`,
 * `padding $space-4`, `$radius-ds-lg`, fundo `$warning-tint` e contorno
 * `$warning-500`.
 *
 * Ordem no `.pen`: ícone `camera` 20 `$warning-fg` · mensagem `$type-body` 500
 * `$fg` · linha de acção (`space_between`) com o CTA a toda a largura
 * (`$warning-700`, `$radius-ds-md`, `padding [10,16]`) e o `x` de dispensar 14
 * `$fg-muted`.
 *
 * A distinção «hoje» vs «atrasado» já é comunicada pela espinha temporal
 * (DashboardCycleHeader) — este banner é o mesmo para os dois estados.
 * A chave de dismiss inclui `cycle.end` para reaparecer no ciclo seguinte.
 */
export function DashboardCycleCloseBanner({ cycle, remindersEnabled = true, className }) {
  const dismissKey = cycle?.end ? `cycle-close-${cycle.end}` : null;
  const [justDismissed, setJustDismissed] = useState(false);
  const dismissed =
    justDismissed || (Boolean(dismissKey) && readDismissedAlertIds().has(dismissKey));

  if (!remindersEnabled || !isCycleCloseDue(cycle) || dismissed) return null;

  const copy = pt.dashboard.cycleClose.dueToday;

  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col items-start gap-space-3 rounded-ds-lg border border-warning-500 bg-warning-tint p-space-4 dark:bg-warning-tint/20',
        className,
      )}
    >
      <Camera className="h-5 w-5 shrink-0 text-warning-fg" aria-hidden />

      <p className="type-body w-full font-medium text-foreground">{copy.messageFallback}</p>

      <div className="flex w-full min-w-0 items-center justify-between gap-space-3">
        <Link
          to={paths.selfieNew}
          /* `$warning-tint` sobre `$warning-700` fica ilegível em dark (a var
             só guarda RGB, sem o alfa do `.pen`) — `warning-50` é o mesmo valor
             em light e mantém contraste nos dois temas. */
          className="type-body inline-flex w-full items-center justify-center rounded-ds-md bg-warning-700 px-space-4 py-space-2-5 font-semibold text-warning-50 transition hover:bg-warning-700/90"
        >
          {copy.cta}
        </Link>

        {dismissKey ? (
          <button
            type="button"
            onClick={() => {
              dismissAlertId(dismissKey);
              setJustDismissed(true);
            }}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={pt.alerts.dismiss}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default DashboardCycleCloseBanner;
